'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { BOARD_CONSTANTS, PERIOD_CATEGORIES } from '@/constants/board';
import { BoardPagination } from './BoardPagination';
import { SearchFilter } from './SearchFilter';
import { formatDate } from '@/utils';
import type { BoardListProps, SearchFilter as SearchFilterType, BoardCategory } from '@/types/board';
import Attachment from '@public/svgs/attachment.svg'
import SearchIcon from '@public/svgs/search.svg';
import PinIcon from '@public/svgs/pin.svg';

// 기본 칼럼 너비 상수 (비율 기반)
const BASE_COLUMN_WIDTHS = {
  NUMBER: 'w-2/22',
  TITLE: 'w-11/22',
  AUTHOR: 'w-2/22',
  VIEWS: 'w-3/22',
  FILE: 'w-1/22',
  DATE: 'w-3/22'
} as const;

// 공고기간 칼럼이 포함된 칼럼 너비 상수
const PERIOD_COLUMN_WIDTHS = {
  NUMBER: 'w-2/24',
  TITLE: 'w-8/24',
  AUTHOR: 'w-2/24',
  VIEWS: 'w-2/24',
  FILE: 'w-1/24',
  PERIOD: 'w-5/24',
  DATE: 'w-2/24'
} as const;
interface BoardListPropsWithCategory extends BoardListProps {
  category: BoardCategory;
}

export function BoardList({ category, posts, totalCount, currentPage, totalPages }: BoardListPropsWithCategory) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchFilter, setSearchFilter] = useState<SearchFilterType>({
    searchType: (searchParams.get('searchType') as SearchFilterType['searchType']) || 'all',
    searchKeyword: searchParams.get('searchKeyword') || ''
  });

  // 공고기간이 필요한 카테고리인지 확인
  const needsPeriodColumn = PERIOD_CATEGORIES.includes(category.id as 'recruit' | 'rise-projects');

  // URL 파라미터가 변경될 때 검색 필터 상태 동기화 (초기 로드 시에만)
  useEffect(() => {
    const urlSearchType = searchParams.get('searchType') as SearchFilterType['searchType'];
    const urlSearchKeyword = searchParams.get('searchKeyword');

    setSearchFilter({
      searchType: urlSearchType || 'all',
      searchKeyword: urlSearchKeyword || ''
    });
  }, [searchParams]);

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams.toString());

    // 검색 파라미터 설정
    if (searchFilter.searchType !== 'all') {
      params.set('searchType', searchFilter.searchType);
    } else {
      params.delete('searchType');
    }

    if (searchFilter.searchKeyword.trim()) {
      params.set('searchKeyword', searchFilter.searchKeyword.trim());
    } else {
      params.delete('searchKeyword');
    }

    // 검색 시 페이지를 1로 리셋
    params.set('page', '1');

    // URL 업데이트
    router.push(`?${params.toString()}`);
  };

  const handleSearchFilterChange = (filter: SearchFilterType) => {
    setSearchFilter(filter);
  };


  // 게시판 카테고리에 따른 상세 페이지 경로 생성
  const getPostDetailPath = (postId: string) => {
    switch (category.id) {
      case 'notices':
        return `/notices/${postId}`;
      case 'recruit':
        return `/recruit/${postId}`;
      case 'newsletter':
        return `/newsletters/${postId}`;
      case 'rise-projects':
        return `/announcements/${postId}`;
      case 'resources':
        return `/resources/${postId}`;
      case 'living-research':
        return `/living-research/${postId}`;
      default:
        return `/notices/${postId}`;
    }
  };

  const getSearchTypeLabel = (searchType: SearchFilterType['searchType']) => {
    switch (searchType) {
      case 'title':
        return '제목';
      case 'content':
        return '내용';
      case 'author':
        return '작성자';
      default:
        return '전체';
    }
  };



  return (
    <div>
      {/* 검색 상태 표시 */}
      {searchParams.get('searchKeyword') && (
        <div className="mb-4 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SearchIcon className="w-4 h-4 text-blue-600" />
              <span className="text-blue-800 font-medium">
                &quot;{searchParams.get('searchKeyword')}&quot; 검색 결과
              </span>
            </div>
            <button
              onClick={() => {
                setSearchFilter({ searchType: 'all', searchKeyword: '' });
                router.push(`?page=1`);
              }}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              검색 초기화
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 mb-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-row gap-3 items-center text-base sm:text-lg text-foreground">
          <p>전체 <b>{totalCount}</b>건</p>
          <div className="w-0.5 h-4.5 bg-border/70" />
          <p>현재페이지 <b>{currentPage}/{totalPages || 1}</b></p>
        </div>

        <SearchFilter
          searchFilter={searchFilter}
          onSearchFilterChange={handleSearchFilterChange}
          onSearch={handleSearch}
        />
      </div>

      <div className="block sm:hidden mb-16">
        {posts.length > 0 ? (
          <div className="space-y-3">
            {posts.map((post, _) => (
              <div key={post.id} className={`border border-border rounded-lg p-4 hover:bg-gray-50 ${post.isPinned ? 'bg-yellow-50 border-[#FFFFEC]' : ''
                }`}>
                <div className="flex items-start justify-between mb-3">
                  <Link
                    href={getPostDetailPath(post.id)}
                    className="flex-1 text-sm font-medium text-foreground hover:text-primary hover:underline line-clamp-2 mr-3"
                  >
                    {category.id === 'resources' && post.category ? (
                      <span className="inline-flex items-center flex-wrap">
                        {post.isPinned && (
                          <span className="inline-block mr-2 items-center justify-center -mb-0.5">
                            <PinIcon className="size-4 text-primary" />
                          </span>
                        )}
                        <span className="inline-block text-primary mr-1 font-bold text-nowrap">
                          [{post.category}]
                        </span>
                        <span className="line-clamp-2">{post.title}</span>
                      </span>
                    ) : (
                      <span className="line-clamp-2">{post.title}</span>
                    )}
                  </Link>
                  {post.hasFile && (
                    <Attachment className="h-4 w-4 text-foreground flex-shrink-0" />
                  )}
                </div>

                {needsPeriodColumn && (
                  <div className="text-xs text-muted -mt-2 mb-1">
                    {post.startDate && post.endDate ? (
                      <span>공고기간: {formatDate(post.startDate)} ~ {formatDate(post.endDate)}</span>
                    ) : (
                      <span>공고기간: 기간 미설정</span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-muted">
                  <span>{post.author}</span>
                  <div className="flex items-center gap-3">
                    <span>{new Date(post.createdAt).toISOString().split('T')[0]}</span>
                    <span>조회수 {(post.views || 0).toLocaleString()}</span>
                  </div>
                </div>

              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-foreground text-sm">
            {searchParams.get('searchKeyword') ? (
              <div className="space-y-3">
                <div className="text-lg font-medium text-foreground">
                  검색 결과가 없습니다
                </div>
                <div className="text-sm text-muted space-y-1">
                  <p>검색어: &quot;{searchParams.get('searchKeyword')}&quot; ({getSearchTypeLabel((searchParams.get('searchType') as SearchFilterType['searchType']) || 'all')})</p>
                  <p>다른 검색어나 검색 조건을 시도해보세요.</p>
                </div>
                <button
                  onClick={() => {
                    setSearchFilter({ searchType: 'all', searchKeyword: '' });
                    router.push(`?page=1`);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-border text-foreground rounded-lg hover:bg-gray-50 transition-colors text-sm duration-100"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  검색 초기화
                </button>
              </div>
            ) : (
              '게시글이 없습니다.'
            )}
          </div>
        )}
      </div>

      <div className="hidden sm:block border-t-[1.5px] border-b-[1.5px] mb-16 sm:mb-20 lg:mb-23 border-foreground text-sm sm:text-base">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] table-fixed">
            <thead className="bg-[#FFEEF4]">
              <tr>
                <th className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.NUMBER : BASE_COLUMN_WIDTHS.NUMBER} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center font-semibold text-xs sm:text-sm lg:text-base text-nowrap`}>번호</th>
                <th className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.TITLE : BASE_COLUMN_WIDTHS.TITLE} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-left font-semibold text-xs sm:text-sm lg:text-base text-nowrap`}>제목</th>
                <th className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.AUTHOR : BASE_COLUMN_WIDTHS.AUTHOR} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center font-semibold text-xs sm:text-sm lg:text-base text-nowrap`}>작성자</th>
                <th className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.VIEWS : BASE_COLUMN_WIDTHS.VIEWS} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center font-semibold text-xs sm:text-sm lg:text-base text-nowrap`}>조회수</th>
                <th className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.FILE : BASE_COLUMN_WIDTHS.FILE} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center font-semibold text-xs sm:text-sm lg:text-base text-nowrap`}>파일</th>
                {needsPeriodColumn && (
                  <th className={`${PERIOD_COLUMN_WIDTHS.PERIOD} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center font-semibold text-xs sm:text-sm lg:text-base text-nowrap`}>공고기간</th>
                )}
                <th className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.DATE : BASE_COLUMN_WIDTHS.DATE} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center font-semibold text-xs sm:text-sm lg:text-base text-nowrap`}>작성일</th>
              </tr>
            </thead>
            <tbody>
              {posts.length > 0 ? (
                posts.map((post, index) => (
                  <tr key={post.id} className={`border-t border-border ${post.isPinned ? 'bg-[#FFFFEC] hover:bg-[#fafadf]' : 'hover:bg-gray-50'
                    }`}>
                    <td className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.NUMBER : BASE_COLUMN_WIDTHS.NUMBER} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center text-foreground text-xs sm:text-sm lg:text-base`}>
                      {post.isPinned ? (
                        <div className="flex items-center justify-center">
                          <PinIcon className="sm:size-4 lg:size-5 text-primary" />
                        </div>
                      ) : (
                        totalCount - (currentPage - 1) * BOARD_CONSTANTS.ITEMS_PER_PAGE - index
                      )}
                    </td>
                    <td className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.TITLE : BASE_COLUMN_WIDTHS.TITLE} px-2 sm:px-3 lg:px-4 py-0 sm:py-0 text-foreground text-xs sm:text-sm lg:text-base`}>
                      <Link
                        href={getPostDetailPath(post.id)}
                        className="group text-left hover:underline block line-clamp-1"
                        title={post.title}
                      >
                        {category.id === 'resources' && post.category ? (
                          <span className="inline-flex items-center">
                            <span className="inline-block font-bold text-primary mr-1 text-nowrap">
                              [{post.category}]
                            </span>
                            <span className="line-clamp-1">{post.title}</span>
                          </span>
                        ) : (
                          <span className="line-clamp-1">{post.title}</span>
                        )}
                      </Link>
                    </td>
                    <td className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.AUTHOR : BASE_COLUMN_WIDTHS.AUTHOR} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center text-foreground text-xs sm:text-sm lg:text-base`}>
                      {post.author}
                    </td>
                    <td className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.VIEWS : BASE_COLUMN_WIDTHS.VIEWS} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center text-foreground text-xs sm:text-sm lg:text-base`}>
                      {(post.views || 0).toLocaleString()}
                    </td>
                    <td className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.FILE : BASE_COLUMN_WIDTHS.FILE} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center text-foreground text-xs sm:text-sm lg:text-base`}>
                      {post.hasFile && (
                        <div className="flex items-center justify-center space-x-1">
                          <Attachment className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
                        </div>
                      )}
                    </td>
                    {needsPeriodColumn && (
                      <td className={`${PERIOD_COLUMN_WIDTHS.PERIOD} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center text-foreground text-xs sm:text-sm lg:text-base`}>
                        {post.startDate && post.endDate ? (
                          <span className="text-base">
                            {formatDate(post.startDate)} ~ {formatDate(post.endDate)}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                    )}
                    <td className={`${needsPeriodColumn ? PERIOD_COLUMN_WIDTHS.DATE : BASE_COLUMN_WIDTHS.DATE} px-2 sm:px-3 lg:px-4 py-3 sm:py-4 text-center text-foreground text-xs sm:text-sm lg:text-base`}>
                      {new Date(post.createdAt).toISOString().split('T')[0]}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={needsPeriodColumn ? 7 : 6} className="px-4 py-8 text-center text-foreground text-sm sm:text-base">
                    {searchParams.get('searchKeyword') ? (
                      <div className="space-y-3">
                        <div className="text-lg font-medium text-foreground">
                          검색 결과가 없습니다
                        </div>
                        <div className="text-sm text-muted space-y-1">
                          <p>검색어: &quot;{searchParams.get('searchKeyword')}&quot; ({getSearchTypeLabel((searchParams.get('searchType') as SearchFilterType['searchType']) || 'all')})</p>
                          <p>다른 검색어나 검색 조건을 시도해보세요.</p>
                        </div>
                        <button
                          onClick={() => {
                            setSearchFilter({ searchType: 'all', searchKeyword: '' });
                            router.push(`?page=1`);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-border text-foreground rounded-lg hover:bg-gray-50 transition-colors text-sm duration-100"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          검색 초기화
                        </button>
                      </div>
                    ) : (
                      '게시글이 없습니다.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BoardPagination
        currentPage={currentPage}
        totalPages={totalPages}
      />
    </div>
  );
} 
